import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api from '../services/api';
import { Contractor } from '../types';
import { useAuth } from './AuthContext';

interface ContractorContextType {
  contractor: Contractor | null;
  loading: boolean;
  refreshContractor: () => Promise<void>;
}

const ContractorContext = createContext<ContractorContextType | undefined>(undefined);

export const ContractorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [contractor, setContractor] = useState<Contractor | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchContractor = useCallback(async () => {
    // Only fetch if user is a contractor
    if (user?.role !== 'CONTRACTOR') {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/contractor/profile');
      setContractor(response.data);
    } catch (error) {
      console.error('Failed to fetch contractor profile:', error);
      setContractor(null);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === 'CONTRACTOR') {
      fetchContractor();
    } else {
      setLoading(false);
    }
  }, [user, fetchContractor]);

  const refreshContractor = async () => {
    await fetchContractor();
  };

  return (
    <ContractorContext.Provider value={{ contractor, loading, refreshContractor }}>
      {children}
    </ContractorContext.Provider>
  );
};

export const useContractor = () => {
  const context = useContext(ContractorContext);
  if (context === undefined) {
    throw new Error('useContractor must be used within a ContractorProvider');
  }
  return context;
};


